import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// ReceivePhotoThumb — renders a receive_log photo thumbnail.
//
// The receive-photos bucket is private (migration 0058), so photo_url
// now holds an object PATH, not a public URL. We exchange the path for a
// short-TTL signed URL via the sign-receive-photo edge function, then
// render it. The signed URL is also the target of the "open full size"
// link.
//
// States:
//   - null/empty photoPath        → render nothing
//   - signing in flight / failed  → neutral placeholder (no raw error)
//   - signed                      → thumbnail linking to the signed URL
//
// All hooks run before any conditional return (React #310).
export default function ReceivePhotoThumb({ photoPath, alt }) {
  const [signedUrl, setSignedUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function sign() {
      setSignedUrl(null)
      setFailed(false)
      if (!photoPath) return
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
        const { data: sessData } = await supabase.auth.getSession()
        const token = sessData?.session?.access_token || anonKey

        const resp = await fetch(`${supabaseUrl}/functions/v1/sign-receive-photo`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'authorization': `Bearer ${token}`,
            'apikey': anonKey,
          },
          body: JSON.stringify({ path: photoPath }),
        })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const data = await resp.json()
        if (!data?.signedUrl) throw new Error('no signed url')
        if (!cancelled) setSignedUrl(data.signedUrl)
      } catch (_e) {
        if (!cancelled) setFailed(true)
      }
    }

    sign()
    return () => { cancelled = true }
  }, [photoPath])

  // Nothing to show.
  if (!photoPath) return null

  // Neutral placeholder while signing, or on failure — never a raw error.
  if (!signedUrl) {
    return (
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-black/10 bg-black/5 text-black/30 ${
          failed ? '' : 'animate-pulse'
        }`}
        aria-hidden="true"
        title={failed ? 'Photo unavailable' : 'Loading photo…'}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>
    )
  }

  return (
    <a
      href={signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="View photo"
      className="block h-12 w-12 shrink-0 overflow-hidden rounded-md border border-black/10 bg-black/5"
    >
      <img
        src={signedUrl}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </a>
  )
}
